//------------------------------------------------
//
//	ReU: チャプターの章跨ぎ固定
//	設定「チャプターを固定 → 次のチャプターまで固定」(.chapterPin) のチャプターを、
//	次の章が来るまで画面に固定し、章の終わりで上へ押し出す。
//	「セクション内で固定」は CSS の sticky だけで完結するのでここでは触らない。
//	JS が動かない環境では sticky のまま＝セクション内固定に自然に落ちる。
//
//------------------------------------------------
(() => {
    // 章の先頭になれるチャプター。カルーセル内のスライド見出しは対象外にするため、置き場所で限定する
    const CHAPTER_SELECTOR = '.chapterTrack > .chapter, .sectionLayout__content > .chapter';

    let chapters = [];
    let ticking = false;
    let lastWidth = 0;

    //------------------------------------------------
    //	章の組み立て
    //	章＝「次のチャプターまで固定」のセクションから、
    //	　　次のチャプター名を持つセクション、または
    //	　　チャプターの列を持たない全幅セクション（CTAバナー等）が現れる手前まで。
    //------------------------------------------------
    const readSections = () => {
        const main = document.getElementById('MainContent');
        if (!main) return [];

        return Array.from(main.children).map((wrap) => {
            const chapter = wrap.querySelector(CHAPTER_SELECTOR);
            return {
                wrap,
                chapter,
                hasColumn: wrap.querySelector('.hasLayoutIndent') !== null,
                isPinType: chapter !== null && chapter.closest('.chapterPin') !== null,
            };
        });
    };

    const build = () => {
        const sections = readSections();
        const built = [];

        sections.forEach((section, index) => {
            if (!section.isPinType) return;

            let lastIndex = index;
            for (let i = index + 1; i < sections.length; i += 1) {
                const next = sections[i];
                if (next.chapter || !next.hasColumn) break;
                lastIndex = i;
            }

            // 章が1セクションで終わるならCSSのstickyで足りる
            if (lastIndex === index) return;

            built.push({
                el: section.chapter,
                track: section.chapter.parentElement,
                endEl: sections[lastIndex].wrap,
            });
        });

        return built;
    };

    //------------------------------------------------
    //	章の下端になる要素を探す
    //	セクションの箱の下端ではなく中身の下端で止めたいので、
    //	いちばん下まで伸びている中身の要素を辿って覚えておく（下の余白を含めないため）
    //------------------------------------------------
    const BLOCK_LEVEL = /(block|flex|grid|list-item|table|flow-root)/;

    // 要素の内側の下端（自分の下パディング・下ボーダーを除いた位置）
    const innerBottom = (el) => {
        const style = window.getComputedStyle(el);
        return {
            offset: (parseFloat(style.paddingBottom) || 0) + (parseFloat(style.borderBottomWidth) || 0),
            bottom: el.getBoundingClientRect().bottom
                - (parseFloat(style.paddingBottom) || 0)
                - (parseFloat(style.borderBottomWidth) || 0),
        };
    };

    const findContentEnd = (root) => {
        let el = root;
        let inner = innerBottom(root);

        for (let depth = 0; depth < 20; depth += 1) {
            let lowest = null;
            let lowestBottom = -Infinity;

            Array.prototype.forEach.call(el.children, (child) => {
                const style = window.getComputedStyle(child);
                if (style.display === 'none' || !BLOCK_LEVEL.test(style.display)) return;
                if (style.position === 'fixed' || style.position === 'absolute') return;

                const rect = child.getBoundingClientRect();
                if (rect.height <= 0) return;
                if (rect.bottom <= lowestBottom) return;

                lowest = child;
                lowestBottom = rect.bottom;
            });

            // 子が親の内側より上で終わるなら、親の箱じたいが下まで伸びている
            // （比率固定の画像枠など）。そこで止める
            if (!lowest || lowestBottom < inner.bottom - 1) break;

            el = lowest;
            inner = innerBottom(el);
        }

        return { el, offset: inner.offset };
    };

    //------------------------------------------------
    //	寸法の取り込み
    //	スクロールで変わらない値だけをここで持つ。位置はスクロールのたびに測る
    //------------------------------------------------
    const measure = (chapter) => {
        const { el, track } = chapter;

        el.style.position = '';
        el.style.top = '';
        el.style.left = '';
        el.style.width = '';
        el.classList.remove('isPinned');

        const trackStyle = window.getComputedStyle(track);
        const elStyle = window.getComputedStyle(el);

        chapter.padTop = parseFloat(trackStyle.paddingTop) || 0;
        chapter.padLeft = parseFloat(trackStyle.paddingLeft) || 0;
        chapter.width = el.offsetWidth;
        chapter.height = el.offsetHeight;
        const contentEnd = findContentEnd(chapter.endEl);
        chapter.endContent = contentEnd.el;
        chapter.endOffset = contentEnd.offset;
        // 固定位置は sticky と同じ指定（ヘッダー高さ＋3.5rem）をCSSから読む
        chapter.pinTop = parseFloat(elStyle.top) || 0;
    };

    const refresh = () => {
        chapters = build();
        chapters.forEach(measure);
        update();
    };

    //------------------------------------------------
    //	状態の反映
    //	1章あたり2回だけ位置を読み、読み終えてからまとめて書く
    //------------------------------------------------
    const update = () => {
        if (!chapters.length) return;

        const states = chapters.map((chapter) => {
            const trackRect = chapter.track.getBoundingClientRect();
            const wall = chapter.endContent.getBoundingClientRect().bottom - chapter.endOffset;
            const naturalTop = trackRect.top + chapter.padTop;

            return {
                chapter,
                pin: naturalTop <= chapter.pinTop,
                // 章の終わりでは、章の中身の下端にチャプターの下端を合わせて上へ押し出す
                top: Math.min(chapter.pinTop, wall - chapter.height),
                left: trackRect.left + chapter.padLeft,
            };
        });

        states.forEach((state) => {
            const { el } = state.chapter;

            if (!state.pin) {
                if (!el.classList.contains('isPinned')) return;
                el.classList.remove('isPinned');
                el.style.position = '';
                el.style.top = '';
                el.style.left = '';
                el.style.width = '';
                return;
            }

            el.classList.add('isPinned');
            el.style.position = 'fixed';
            el.style.top = `${state.top}px`;
            el.style.left = `${state.left}px`;
            el.style.width = `${state.chapter.width}px`;
        });
    };

    const onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
            ticking = false;
            update();
        });
    };

    // 画面の高さだけの変化（スマホのアドレスバーの出入り）では測り直さない。
    // 幅が変わったときだけ寸法を取り直す
    const onResize = () => {
        if (window.innerWidth === lastWidth) {
            update();
            return;
        }
        lastWidth = window.innerWidth;
        refresh();
    };

    const init = () => {
        lastWidth = window.innerWidth;
        refresh();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize);
        // 画像やWebフォントで高さが変わったら測り直す
        window.addEventListener('load', refresh);
        document.addEventListener('shopify:section:load', refresh);
        document.addEventListener('shopify:section:unload', refresh);
        document.addEventListener('shopify:section:reorder', refresh);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
